// Reveal answer module
// Converts {{reveal-answer: Title}} ... {{correct:}} / {{wrong:}} ... {{/reveal-answer}}

import { processTooltipReferences } from './footnotes.js';

/**
 * @param {Element} container
 * @param {Map}     footnotesMap
 */
export function processRevealAnswerBlocks(container, footnotesMap) {
  const allElements = Array.from(container.children);
  const startRegex = /\{\{reveal-answer:\s*([^}]+)\}\}/;
  const endRegex   = /\{\{\/reveal-answer\}\}/;
  const correctRegex = /\{\{correct:\s*([^}]+)\}\}/;
  const wrongRegex   = /\{\{wrong:\s*([^}]+)\}\}/;

  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const startMatch = (element.textContent || '').match(startRegex);
    if (!startMatch) continue;

    const title = startMatch[1].trim();
    let endIndex = -1, endElement = null;

    for (let j = i; j < allElements.length; j++) {
      if ((allElements[j].textContent || '').match(endRegex)) {
        endIndex = j; endElement = allElements[j]; break;
      }
    }

    if (endIndex === -1) {
      console.warn(`[Reveal Answer] "{{reveal-answer: ${title}}}" has no closing {{/reveal-answer}}.`);
      continue;
    }

    // ── Collect elements between markers ──────────────────────────────────────
    const contentElements = [];
    const elementsToRemove = [];

    const startHTML = element.innerHTML || '';
    const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const startHTMLMatch = startHTML.match(
      new RegExp(`\\{\\{reveal-answer:\\s*${escapedTitle}\\}\\}\\s*(.+)`, 's')
    );

    if (startHTMLMatch?.[1]?.trim()) {
      const tmp = document.createElement('div');
      tmp.innerHTML = startHTMLMatch[1].trim();
      Array.from(tmp.children).forEach(c => contentElements.push(c));
    }

    if (element.textContent.trim() === startMatch[0].trim()) {
      elementsToRemove.push(element);
    } else if (startHTMLMatch) {
      const updated = startHTML.replace(startHTMLMatch[0], '').trim();
      updated ? (element.innerHTML = updated) : elementsToRemove.push(element);
    }

    for (let k = i + 1; k < endIndex; k++) {
      contentElements.push(allElements[k]);
      elementsToRemove.push(allElements[k]);
    }

    const endHTML  = endElement.innerHTML || '';
    const endText  = endElement.textContent || '';
    const endHTMLMatch = endHTML.match(/(.+?)\s*\{\{\/reveal-answer\}\}/s);

    if (endHTMLMatch?.[1]?.trim()) {
      const tmp = document.createElement('div');
      tmp.innerHTML = endHTMLMatch[1].trim();
      Array.from(tmp.children).forEach(c => contentElements.push(c));
    }

    if (endText.trim() === '{{/reveal-answer}}') {
      elementsToRemove.push(endElement);
    } else if (endHTML.includes('{{/reveal-answer}}')) {
      const updated = endHTMLMatch
        ? endHTML.replace(endHTMLMatch[0], endHTMLMatch[1] || '').trim()
        : endHTML.replace(/\{\{\/reveal-answer\}\}/g, '').trim();
      updated ? (endElement.innerHTML = updated) : elementsToRemove.push(endElement);
    }

    // ── Parse answers ──────────────────────────────────────────────────────────
    const answers = [];
    contentElements.forEach(el => {
      const text = el.textContent || '';
      const correctMatch = text.match(correctRegex);
      const wrongMatch   = text.match(wrongRegex);
      const matched      = correctMatch || wrongMatch;
      if (!matched) return;

      const type       = correctMatch ? 'correct' : 'wrong';
      const answerText = matched[1].trim();

      if (text.trim() === matched[0].trim()) {
        if (!elementsToRemove.includes(el)) elementsToRemove.push(el);
      } else {
        const updated = el.innerHTML.replace(matched[0], '').trim();
        updated ? (el.innerHTML = updated) : elementsToRemove.push(el);
      }
      answers.push({ type, text: answerText });
    });

    if (!answers.length) {
      console.warn(`[Reveal Answer] "{{reveal-answer: ${title}}}" has no {{correct:}} or {{wrong:}} answers.`);
    }

    // ── Build DOM ──────────────────────────────────────────────────────────────
    const block = document.createElement('div');
    block.className = 'reveal-answer';

    const headerBtn = document.createElement('button');
    headerBtn.className = 'reveal-answer__header';
    headerBtn.setAttribute('type', 'button');
    headerBtn.setAttribute('aria-expanded', 'false');

    const titleSpan = document.createElement('span');
    titleSpan.className = 'reveal-answer__title';
    titleSpan.textContent = title;
    headerBtn.appendChild(titleSpan);

    const iconSpan = document.createElement('span');
    iconSpan.className = 'reveal-answer__icon';
    iconSpan.setAttribute('aria-hidden', 'true');
    headerBtn.appendChild(iconSpan);

    const answersOl = document.createElement('ol');
    answersOl.className = 'reveal-answer__answers';

    answers.forEach(answer => {
      const li = document.createElement('li');
      li.className = `reveal-answer__answer reveal-answer__answer--${answer.type}`;
      const span = document.createElement('span');
      span.className = 'reveal-answer__answer-text';
      span.textContent = answer.text;
      li.appendChild(span);
      answersOl.appendChild(li);
    });

    block.appendChild(headerBtn);
    block.appendChild(answersOl);

    // Insert / replace
    if (elementsToRemove.includes(element)) {
      element.replaceWith(block);
    } else {
      element.insertAdjacentElement('afterend', block);
    }

    elementsToRemove.forEach(el => { if (el.parentNode && el !== element) el.remove(); });

    // Tooltip refs inside title area
    if (footnotesMap) processTooltipReferences(block, footnotesMap);

    // Click handler
    headerBtn.addEventListener('click', () => {
      const expanded = headerBtn.getAttribute('aria-expanded') === 'true';
      headerBtn.setAttribute('aria-expanded', String(!expanded));
      block.classList.toggle('is-open', !expanded);
    });

    i = endIndex;
  }
}
